/**
 * §1 Baseline 2025 — the filter bar at the top of the section (client, 2026-09-16).
 *
 * Three controls, and each one answers a question the old compact rows could not:
 *
 * * **Sheet** — the survey is ten workbook sheets and 58 series. A reader who came for
 *   land tenure should not scroll past agroforestry to reach it.
 * * **View by** — countries or watersheds. It is a `Seg` rather than a third select
 *   because it is the section's *mode*: both options are always available and the reader
 *   flips between them, which is exactly what §F2 gives the segmented control to.
 * * **Find a series** — a name search, debounced so that typing does not re-plan and
 *   re-draw forty charts on every keystroke.
 *
 * Nothing here is persisted. The section is one screen of one page and the filter is how a
 * reader reads it now; a sheet choice restored a week later from `localStorage` would be a
 * page that silently hides nine tenths of the survey for reasons the reader cannot see.
 * The page's own scope (the country/watershed selects above) is *not* duplicated here — the
 * two compose, and `baselineAxis` is where that composition is written down.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DashboardBaselineSheet } from '../../lib/api';
import { Seg } from '../../components/Seg';
import { Icon } from '../../lib/icons';
import type { BaselineView } from './baseline';

/** How long the search waits after the last keystroke. Long enough to skip a word, short
 *  enough that the charts feel attached to the box. */
export const BASELINE_SEARCH_DEBOUNCE = 200;

export interface BaselineFilterProps {
  sheets: DashboardBaselineSheet[];
  sheetId: string;
  onSheet: (id: string) => void;
  /**
   * §3 — every round the survey has stored, and the one the **server answered with**.
   *
   * Not the one the page asked for: an unknown `?baseline_period=` is ignored and answered
   * with the latest round, so reading the select's value off the request would leave it
   * naming a year the figures below are not from.
   */
  periods: number[];
  period: number;
  onPeriod?: (period: number) => void;
  view: BaselineView;
  onView: (view: BaselineView) => void;
  /** The committed (debounced) query — the section's own state. */
  query: string;
  onQuery: (q: string) => void;
}

export function BaselineFilter({
  sheets,
  sheetId,
  onSheet,
  periods,
  period,
  onPeriod,
  view,
  onView,
  query,
  onQuery,
}: BaselineFilterProps) {
  const { t } = useTranslation();
  // The box is uncontrolled-ish: the input owns what is typed, the section owns what is
  // searched, and the timer is the only thing between them.
  const [text, setText] = useState(query);
  const commit = useRef(onQuery);
  commit.current = onQuery;

  useEffect(() => {
    if (text === query) return undefined;
    const id = window.setTimeout(() => commit.current(text), BASELINE_SEARCH_DEBOUNCE);
    return () => window.clearTimeout(id);
  }, [text, query]);

  return (
    <div className="bl-filter" data-testid="baseline-filter" role="group" aria-label={t('dashboard.baseline.filter.label')}>
      <label className="bl-filter-field">
        <span>{t('dashboard.baseline.filter.sheet')}</span>
        <select
          className="std"
          data-testid="baseline-filter-sheet"
          value={sheetId}
          onChange={(e) => onSheet(e.target.value)}
        >
          <option value="all">{t('dashboard.baseline.filter.allSheets')}</option>
          {sheets.map((sheet) => (
            <option key={sheet.group_id} value={sheet.group_id}>
              {sheet.label}
            </option>
          ))}
        </select>
      </label>

      {/* Always on the bar, disabled while the survey has run once: a control that appears
          the day a second round lands is one no reader has learned to look for, and its
          absence is indistinguishable from a page that lost it. */}
      <label className="bl-filter-field">
        <span>{t('dashboard.baseline.period')}</span>
        <select
          className="std"
          data-testid="baseline-period"
          value={String(period)}
          disabled={periods.length < 2 || !onPeriod}
          onChange={(e) => onPeriod?.(Number(e.target.value))}
        >
          {periods.map((p) => (
            <option key={p} value={String(p)}>
              {p}
            </option>
          ))}
        </select>
      </label>

      <div className="bl-filter-field">
        <span>{t('dashboard.baseline.filter.view')}</span>
        <span data-testid="baseline-filter-view">
          <Seg
            options={[
              {
                value: 'countries',
                label: t('dashboard.baseline.filter.countries'),
                testId: 'baseline-filter-view-countries',
              },
              {
                value: 'watersheds',
                label: t('dashboard.baseline.filter.watersheds'),
                testId: 'baseline-filter-view-watersheds',
              },
            ]}
            value={view}
            onChange={onView}
            ariaLabel={t('dashboard.baseline.filter.view')}
          />
        </span>
      </div>

      <label className="bl-filter-field bl-filter-q">
        <span>{t('dashboard.baseline.filter.search')}</span>
        <span className="bl-filter-input">
          <Icon name="search" size={13} />
          <input
            className="std"
            type="search"
            data-testid="baseline-filter-q"
            value={text}
            placeholder={t('dashboard.baseline.filter.searchPlaceholder')}
            onChange={(e) => setText(e.target.value)}
          />
        </span>
      </label>
    </div>
  );
}

export default BaselineFilter;
